import { useNodes, useReactFlow, useViewport } from '@xyflow/react';
import {
  Minus as ZoomMinusIcon,
  Plus as ZoomPlusIcon,
  Minimize,
} from '@sweep-io/sweep-design/dist/icons';
import { Typography, IconButton, Tooltip } from '@sweep-io/sweep-design';
import { Box, Divider, Paper, styled } from '@mui/material';
import { useSweepCanvasFitView } from '../useSweepCanvasFitView';
import { useSweepCanvasPropsCtx } from '../internal-context/SweepCanvasPropsCtx';
import { useState } from 'react';
import { useForwardPinchToSweepCanvas } from '../hooks/useForwardPinchToSweepCanvas';
import { CSS_Z_INDEX_CANVAS_CONTROLS } from '../const/zIndex';

export interface SweepCanvasControlsProps {
  noFitView?: boolean;
}

const StyledPaper = styled(Paper)({
  backgroundColor: 'white',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '8px',
  gap: '8px',
  padding: '8px',
  width: '48px'
});

export const CanvasControls = () => {
  const { noFitView } = useSweepCanvasPropsCtx();
  const [canvasControlsRef, setCanvasControlsRef] = useState<HTMLDivElement | null>(null);

  const reactFlowInstance = useReactFlow();
  const { zoom } = useViewport();
  const { zoomTo } = reactFlowInstance;
  const { fitView } = useSweepCanvasFitView();
  const nodes = useNodes();
  const disableFitViewButton = nodes.length === 0;
  const duration = 400;

  useForwardPinchToSweepCanvas({ element: canvasControlsRef });

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: '16px',
        right: '16px',
        zIndex: CSS_Z_INDEX_CANVAS_CONTROLS,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '8px',
      }}
      ref={setCanvasControlsRef}
    >
      <StyledPaper>
        <Box height="32px" display="flex" alignItems="center">
          <Typography variant="caption">{Math.round(zoom * 100)}%</Typography>
        </Box>

        <Box component="span" data-testid="zoom-out-button">
          <IconButton
            variant="squared"
            size="small"
            onClick={() => {
              if (zoom <= 0.25) {
                zoomTo(0.1, { duration });
              } else {
                zoomTo(zoom - 0.25, { duration });
              }
            }}
          >
            <ZoomMinusIcon size="medium" />
          </IconButton>
        </Box>

        <IconButton
          variant="squared"
          size="small"
          onClick={() => {
            if (zoom <= 0.1) {
              zoomTo(0.25, { duration });
            } else {
              zoomTo(zoom + 0.25, { duration });
            }
          }}
          data-testid="zoom-in-button"
        >
          <ZoomPlusIcon size="medium" />
        </IconButton>

        {!noFitView && (
          <>
            <Divider sx={{ width: '100%' }} />

            <Tooltip title="Fit view" placement="left">
              <Box component="span" data-testid="fit-view-button">
                <IconButton
                  variant="squared"
                  size="small"
                  disabled={disableFitViewButton}
                  onClick={() => {
                    fitView({ duration: 400 });
                  }}
                >
                  <Minimize size="medium" />
                </IconButton>
              </Box>
            </Tooltip>
          </>
        )}
      </StyledPaper>
    </Box>
  );
};
